import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Parse yyyy-mm-dd (or yyyy-mm) as a local calendar date.
 * `new Date("2026-03-01")` is interpreted as UTC midnight and shifts the calendar month in some timezones.
 */
function parseLocalDateFromValue(value) {
  if (!value) return null;
  const s = String(value).split("/")[0].trim();
  const m = s.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = m[3] != null ? Number(m[3]) : 1;
  if (!y || mo < 1 || mo > 12 || day < 1 || day > 31) return null;
  const d = new Date(y, mo - 1, day);
  return isNaN(d.getTime()) ? null : d;
}

const MONTH_ABBREV = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Convert typed month ("Oct", "10", "1") to "01"–"12". Incomplete "1" stays empty (could be 10–12). */
function monthFieldToNumeric(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^\d{1}$/.test(s)) {
    const n = Number(s);
    if (n >= 2 && n <= 9) return String(n).padStart(2, "0");
    return "";
  }
  if (/^\d{2}$/.test(s)) {
    const n = Number(s);
    if (n >= 1 && n <= 12) return String(n).padStart(2, "0");
    return "";
  }
  const lower = s.toLowerCase();
  const idx = MONTH_ABBREV.findIndex((m) => m.toLowerCase() === lower);
  if (idx >= 0) return String(idx + 1).padStart(2, "0");
  return "";
}

/**
 * Reusable lightweight DatePicker
 * - Views: day, month (grid), year (3x4 grid)
 * - Header nav: prev/next, month button, year button (consistent styles)
 * - Year grid: 3 columns x 4 rows with horizontal paging of 12-year blocks
 * - Props mimic native input usage where possible
 *   value: yyyy-mm-dd
 *   onChange: (eventLike: { target: { value: yyyy-mm-dd } })
 *   min?: yyyy-mm-dd
 *   max?: yyyy-mm-dd
 *   disabled?: boolean
 *   darkMode?: boolean
 *   label?: string (optional, shown inside the trigger)
 *   className?: string (applied to trigger button)
 */
export default function DatePicker({
  value,
  onChange,
  min,
  max,
  disabled = false,
  darkMode = false,
  label,
  className = "",
  error,
  minErrorText,
  maxErrorText,
  validateOnType = false,
  required = false,
  monthYearOnly = false,
  showMonthName = false,       // if true, display month name for monthYearOnly
  monthLabelPrefix = "",
  /** When monthYearOnly and value is empty, show this instead of a date label */
  monthYearEmptyLabel = "",
  dropdownAlign = "left",      // "left" | "right" | "auto" - where dropdown aligns; auto = pick side with more space
  dropdownClassName = "",      // extra classes for the floating panel (e.g. z-index inside modals)
  /** Compact pill style for toolbars (no bottom margin, matches Employee dropdowns) */
  compact = false,
  /** Render calendar in document.body so it is not clipped by overflow parents */
  portal = compact,
}) {
  const PANEL_WIDTH = 320;
  const parsed = useMemo(() => {
    if (!value) return new Date();
    const local = parseLocalDateFromValue(value);
    if (local) return local;
    return new Date(String(value).split("/")[0] ?? value);
  }, [value]);
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("left");
  const [view, setView] = useState(monthYearOnly ? "month" : "day");
  const [viewYear, setViewYear] = useState(parsed.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed.getMonth());
  const [inputText, setInputText] = useState("");
  const [typingError, setTypingError] = useState("");
  const [touched, setTouched] = useState(false);
  const MIN_YEAR = 1884;
  const MAX_YEAR = 2150;

  const ref = useRef(null);
  const panelRef = useRef(null);
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);
  const [panelCoords, setPanelCoords] = useState({ top: 0, left: 0 });

  const updatePortalPosition = useCallback(() => {
    if (!ref.current || typeof window === "undefined") return;
    const rect = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const margin = 12;
    const alignSide =
      dropdownAlign === "auto" ? dropdownPosition : dropdownAlign;
    let left = alignSide === "right" ? rect.right - PANEL_WIDTH : rect.left;
    left = Math.max(margin, Math.min(left, vw - PANEL_WIDTH - margin));
    setPanelCoords({ top: rect.bottom + 8, left });
  }, [dropdownAlign, dropdownPosition]);

  useEffect(() => {
    const handler = (e) => {
      const target = e.target;
      if (ref.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open || !portal) return;
    updatePortalPosition();
    const handle = () => updatePortalPosition();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [open, portal, updatePortalPosition]);

  // Auto-position dropdown: prefer anchoring so the panel stays in the viewport (w-80 ≈ 320px).
  useEffect(() => {
    if (!open || dropdownAlign !== "auto" || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const panelW = 320;
    const margin = 12;
    const spaceRight = vw - rect.right - margin;
    const spaceLeft = rect.left - margin;
    // If opening to the right of the trigger's left edge would overflow, anchor to the right instead.
    if (rect.left + panelW > vw - margin && spaceRight < panelW && spaceLeft >= spaceRight) {
      setDropdownPosition("right");
    } else if (spaceRight < panelW && spaceLeft > spaceRight) {
      setDropdownPosition("right");
    } else {
      setDropdownPosition("left");
    }
  }, [open, dropdownAlign]);

  // Run required validation when touched or when value prop changes
  useEffect(() => {
    if (required) {
      const hasValue = Boolean(value && String(value).trim() !== "");
      if (touched && !hasValue) {
        setTypingError("This field is required");
      } else if (hasValue) {
        setTypingError("");
      }
    }
  }, [required, touched, value]);

  const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const minDate = min ? new Date(min) : null;
  const maxDate = max ? new Date(max) : null;
  const allowedMin = stripTime(minDate ?? new Date(MIN_YEAR, 0, 1));
  const allowedMax = stripTime(maxDate ?? new Date(MAX_YEAR, 11, 31));
  const allowedMaxDayKey = allowedMax.getTime();

  // Sync calendar view when value changes (after allowedMin/allowedMax exist)
  useEffect(() => {
    if (value && String(value).trim() !== "") {
      const raw = String(value).split("/")[0] ?? value;
      const d = parseLocalDateFromValue(raw) || new Date(raw);
      if (!isNaN(d)) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    } else if (monthYearOnly) {
      const d = stripTime(allowedMax);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
      setInputText("");
    }
  }, [value, monthYearOnly, allowedMaxDayKey]);

  useEffect(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const last = new Date(viewYear, viewMonth + 1, 0);
    if (last < allowedMin) {
      setViewYear(allowedMin.getFullYear());
      setViewMonth(allowedMin.getMonth());
    } else if (first > allowedMax) {
      setViewYear(allowedMax.getFullYear());
      setViewMonth(allowedMax.getMonth());
    }
  }, [open, allowedMin, allowedMax]);

  const toDisplay = (d) => {
    const day = String(d.getDate()).padStart(2, "0");
    const mmm = MONTH_ABBREV[d.getMonth()];
    const y = d.getFullYear();
    return `${day} ${mmm} ${y}`;
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const formatMonthLabel = (monthIndex, year) => {
    if (!showMonthName) return `${String(monthIndex + 1).padStart(2, "0")}-${year}`;
    // Example outputs:
    // when monthLabelPrefix === "" => "November 2025"
    // when monthLabelPrefix === "Month " => "Month November 2025"
    return `${monthLabelPrefix}${months[monthIndex]} - ${year}`;
  };

  // Initialize input fields when value changes
  useEffect(() => {
    if (value) {
      const parts = String(value).split("/");
      const raw = parts[0];
      const d = parseLocalDateFromValue(raw) || new Date(raw);
      if (!isNaN(d)) {
        if (dayRef.current) dayRef.current.value = String(d.getDate()).padStart(2, "0");
        if (monthRef.current) {
          monthRef.current.value = monthYearOnly
            ? String(d.getMonth() + 1).padStart(2, "0")
            : MONTH_ABBREV[d.getMonth()];
        }
        if (yearRef.current) yearRef.current.value = String(d.getFullYear());
        setInputText(monthYearOnly ? formatMonthLabel(d.getMonth(), d.getFullYear()) : toDisplay(d));
      }
    } else {
      if (dayRef.current) dayRef.current.value = "";
      if (monthRef.current) monthRef.current.value = "";
      if (yearRef.current) yearRef.current.value = "";
      setInputText("");
    }
    setTypingError("");
  }, [value, monthYearOnly]);

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  // Monday-first (0 = Mon … 6 = Sun). JS getDay() is 0 = Sun.
  const firstDayIndex = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7;

  const isBeforeMin = (d) => d < allowedMin;
  const isAfterMax = (d) => d > allowedMax;

  const selectDay = (d) => {
    if (disabled || monthYearOnly) return;
    const date = new Date(viewYear, viewMonth, d);
    const s = stripTime(date);
    if (isBeforeMin(s) || isAfterMax(s)) return;
    
    // FIXED: Update all input fields when selecting from calendar
    if (dayRef.current) dayRef.current.value = String(d).padStart(2, "0");
    if (monthRef.current) monthRef.current.value = MONTH_ABBREV[viewMonth];
    if (yearRef.current) yearRef.current.value = String(viewYear);
    
    setInputText(toDisplay(date));
    onChange?.({ target: { value: toISO(date) } });
    setOpen(false);
    // mark as interacted
    setTouched(true);
    if (required) setTypingError("");
  };

  // Select a specific Date object (used for adjacent month cells)
  const selectDate = (date) => {
    if (disabled || monthYearOnly) return;
    const s = stripTime(date);
    if (isBeforeMin(s) || isAfterMax(s)) return;
    
    // FIXED: Update all input fields when selecting from calendar
    if (dayRef.current) dayRef.current.value = String(date.getDate()).padStart(2, "0");
    if (monthRef.current) monthRef.current.value = MONTH_ABBREV[date.getMonth()];
    if (yearRef.current) yearRef.current.value = String(date.getFullYear());
    
    setInputText(toDisplay(date));
    onChange?.({ target: { value: toISO(date) } });
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth());
    setOpen(false);
    setTouched(true);
    if (required) setTypingError("");
  };

  const parseDisplay = (text) => {
    const t = text.trim();
    let dd;
    let mm;
    let yy;
    let m = t.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})$/);
    if (m) {
      dd = parseInt(m[1], 10);
      mm = parseInt(m[2], 10);
      yy = parseInt(m[3], 10);
    } else {
      m = t.match(/^(\d{1,2})[\s./-]+([A-Za-z]{3,9})[\s./-]+(\d{4})$/);
      if (!m) return null;
      dd = parseInt(m[1], 10);
      const monthNum = monthFieldToNumeric(m[2].slice(0, 3));
      mm = monthNum ? parseInt(monthNum, 10) : NaN;
      yy = parseInt(m[3], 10);
    }
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    const d = new Date(yy, mm - 1, dd);
    if (isNaN(d)) return null;
    if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return d;
  };

  const validateDatePart = (day, monthRaw, year) => {
    const month = monthFieldToNumeric(monthRaw);
    if (!day && !monthRaw && !year) {
      setTypingError("");
      return true;
    }

    // Validate day range immediately
    if (day && (parseInt(day) < 1 || parseInt(day) > 31)) {
      setTypingError("Day must be between 01 and 31");
      return false;
    }

    if (monthRaw && String(monthRaw).length >= 3 && !month && !/^\d+$/.test(String(monthRaw))) {
      setTypingError("Month must be Jan–Dec or 01–12");
      return false;
    }

    // Validate month range immediately
    if (month && (parseInt(month) < 1 || parseInt(month) > 12)) {
      setTypingError("Month must be between 01 and 12");
      return false;
    }

    // Validate year range immediately when we have 4 digits
    if (year && year.length === 4) {
      if(year.length!==4){
        setTypingError("Year must be 4 digits");
        return false;
      }
      const yearNum = parseInt(year);
      if (yearNum < MIN_YEAR || yearNum > MAX_YEAR) {
        setTypingError(`Year must be between ${MIN_YEAR} and ${MAX_YEAR}`);
        return false;
      }
    }

    // VALIDATE FULL DATE AS SOON AS WE HAVE ALL PARTS
    if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
      const formatted = `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
      const parsed = parseDisplay(formatted);
      
      if (!parsed) {
        setTypingError("Invalid date");
        return false;
      }

      const s = stripTime(parsed);
      if (isBeforeMin(s)) {
        setTypingError(minErrorText || `Date must be after ${toDisplay(allowedMin)}`);
        return false;
      } else if (isAfterMax(s)) {
        setTypingError(maxErrorText || `Date must be before ${toDisplay(allowedMax)}`);
        return false;
      }
    }

    setTypingError("");
    return true;
  };

  const handleKeyDown = (e, nextRef, prevRef) => {
    const { selectionStart, selectionEnd, value } = e.target;

    if (e.key === "ArrowRight" && selectionStart === value.length && selectionEnd === value.length) {
      e.preventDefault();
      nextRef?.current?.focus();
      nextRef?.current?.setSelectionRange(0, 0);
    } else if (e.key === "ArrowLeft" && selectionStart === 0 && selectionEnd === 0) {
      e.preventDefault();
      prevRef?.current?.focus();
      prevRef?.current?.setSelectionRange(prevRef.current.value.length, prevRef.current.value.length);
    } else if (e.key === "Backspace" && selectionStart === 0 && selectionEnd === 0 && prevRef) {
      e.preventDefault();
      prevRef.current.focus();
      prevRef.current.setSelectionRange(prevRef.current.value.length, prevRef.current.value.length);
    } else if (e.key === "Delete" && selectionStart === value.length && selectionEnd === value.length && nextRef) {
      e.preventDefault();
      nextRef.current.focus();
      nextRef.current.setSelectionRange(0, 0);
    }
  };

  const handlePaste = (e) => {
      if (monthYearOnly) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digits = paste.replace(/\D/g, "").slice(0, 8);
    
    if (digits.length === 8) {
      const day = digits.slice(0, 2);
      const month = digits.slice(2, 4);
      const year = digits.slice(4, 8);
      
      if (dayRef.current) dayRef.current.value = day;
      if (monthRef.current) {
        const monthNum = parseInt(month, 10);
        monthRef.current.value =
          monthNum >= 1 && monthNum <= 12 ? MONTH_ABBREV[monthNum - 1] : month;
      }
      if (yearRef.current) yearRef.current.value = year;
      
      handlePartChange('year', year);
      yearRef.current?.focus();
    }
  };

  // Fixed: Validate on every change and update calendar accordingly
  const handlePartChange = (field, value) => {
    let dayVal = dayRef.current?.value || "";
    let monthVal = monthRef.current?.value || "";
    let yearVal = yearRef.current?.value || "";

    if (field === 'day' && dayRef.current) {
      const numericValue = value.replace(/\D/g, '').slice(0, 2);
      dayRef.current.value = numericValue;
      dayVal = numericValue;
      if (numericValue.length === 2) {
        monthRef.current?.focus();
        monthRef.current?.select();
      }
    } else if (field === 'month' && monthRef.current) {
      const cleaned = String(value || "").replace(/[^A-Za-z0-9]/g, "");
      if (cleaned && /^\d+$/.test(cleaned)) {
        const numericValue = cleaned.slice(0, 2);
        monthRef.current.value = numericValue;
        monthVal = numericValue;
        if (numericValue.length === 1) {
          const firstDigit = parseInt(numericValue, 10);
          if (firstDigit >= 2 && firstDigit <= 9) {
            setTimeout(() => {
              if (monthRef.current) {
                monthRef.current.value = MONTH_ABBREV[firstDigit - 1];
              }
              yearRef.current?.focus();
              yearRef.current?.select();
            }, 10);
          }
        } else if (numericValue.length === 2) {
          const n = parseInt(numericValue, 10);
          setTimeout(() => {
            if (monthRef.current && n >= 1 && n <= 12) {
              monthRef.current.value = MONTH_ABBREV[n - 1];
            }
            yearRef.current?.focus();
            yearRef.current?.select();
          }, 10);
        }
      } else {
        const letters = cleaned.replace(/[^A-Za-z]/g, "").slice(0, 3);
        const titled = letters
          ? letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase()
          : "";
        monthRef.current.value = titled;
        monthVal = titled;
        const idx = MONTH_ABBREV.findIndex((m) => m.toLowerCase() === titled.toLowerCase());
        if (idx >= 0) {
          monthRef.current.value = MONTH_ABBREV[idx];
          monthVal = MONTH_ABBREV[idx];
          setTimeout(() => {
            yearRef.current?.focus();
            yearRef.current?.select();
          }, 10);
        }
      }
    } else if (field === 'year' && yearRef.current) {
      const numericValue = value.replace(/\D/g, '').slice(0, 4);
      yearRef.current.value = numericValue;
      yearVal = numericValue;
    }

    const currentDay = field === 'day' ? dayVal : (dayRef.current?.value || "");
    const currentMonth = monthFieldToNumeric(monthRef.current?.value || monthVal);
    const currentYear = field === 'year' ? yearVal : (yearRef.current?.value || "");

    if (currentDay && currentMonth && currentYear) {
      const mi = parseInt(currentMonth, 10) - 1;
      setInputText(`${String(currentDay).padStart(2, "0")} ${MONTH_ABBREV[mi]} ${currentYear}`);
    } else {
      setInputText("");
    }

    const isValid = validateDatePart(currentDay, monthRef.current?.value || monthVal, currentYear);

    if (currentMonth.length === 2 && currentYear.length === 4) {
      const monthNum = parseInt(currentMonth, 10);
      const yearNum = parseInt(currentYear, 10);
      if (monthNum >= 1 && monthNum <= 12 && yearNum >= MIN_YEAR && yearNum <= MAX_YEAR) {
        setViewYear(yearNum);
        setViewMonth(monthNum - 1);
      }
    }

    if (isValid && currentDay.length === 2 && currentMonth.length === 2 && currentYear.length === 4) {
      const formatted = `${currentDay.padStart(2, "0")}-${currentMonth}-${currentYear}`;
      const parsed = parseDisplay(formatted);
      if (parsed) {
        setViewYear(parsed.getFullYear());
        setViewMonth(parsed.getMonth());
        onChange?.({ target: { value: toISO(parsed) } });
        setTouched(true);
        if (required) setTypingError("");
      }
    }
  };

  const getYearGridStart = () => viewYear - (viewYear % 12);
  const yearStart = getYearGridStart();
  const years = Array.from({ length: 12 }, (_, i) => yearStart + i);

  const headerBg = darkMode ? "bg-[#2C2C2C]" : "bg-white";
  const headerBorder = darkMode ? "border-[#3C3C3C]" : "border-gray-200";
  const textCol = darkMode ? "text-[#E0E0E0]" : "text-gray-900";
  const subText = darkMode ? "text-gray-400" : "text-gray-500";

  const hasError = Boolean(error || typingError);
  const triggerLine = hasError
    ? "border-b border-red-500"
    : open
      ? "border-b border-redhalo-red"
      : darkMode
        ? "border-b border-[#4C4C4C] focus-within:border-b focus-within:border-redhalo-red"
        : "border-b border-gray-300 focus-within:border-b focus-within:border-redhalo-red";
  const defaultTriggerClass = `block w-full px-3 py-3.5 min-h-[3.25rem] text-sm rounded-none border-0 border-b text-left transition-[border-color] flex items-center ${triggerLine} ${
    darkMode ? "bg-[#3C3C3C] text-[#E0E0E0]" : "bg-white text-gray-900"
  } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`;
  const compactTriggerClass = `appearance-none min-h-0 shrink-0 px-2.5 py-1.5 border rounded-md text-xs font-medium text-left transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer flex items-center gap-1.5 ${
    hasError
      ? "border-red-500"
      : darkMode
        ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
        : "bg-white/95 border-[#f3d6bf] text-gray-900 focus:border-[#ef6f2e]"
  } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`;
  const triggerClass = compact ? compactTriggerClass : defaultTriggerClass;
  const wrapperClass = compact
    ? "relative shrink-0 w-auto min-w-0"
    : "relative mb-4 w-full min-w-0";

  return (
    <div className={wrapperClass} ref={ref}>
      <div
        className={`${triggerClass} gap-2`}
        onMouseDown={(e) => {
          if (disabled) return;
          const t = e.target;
          if (t.closest("input") || t.closest("button")) return;
          // Month-name control toggles on click; skip mousedown so the panel
          // does not open and immediately close on the same press.
          if (t.closest("[data-datepicker-month-label]")) return;
          setOpen((o) => !o);
        }}
      >
        {label && (
          <div
            className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 left-1 rounded-none ${
              darkMode ? "text-gray-400" : "text-gray-800"
            }`}
          >
            {label}
            {required && (
              <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} ml-1`}>*</span>
            )}
          </div>
        )}
        
        <div className="flex min-h-0 flex-1 items-center">
          {/* If month-year only AND showMonthName = true → show MONTH NAME */}
          {monthYearOnly && showMonthName ? (
            <div
              role="button"
              tabIndex={0}
              data-datepicker-month-label
              onClick={() => !disabled && setOpen(o => !o)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpen((o) => !o);
                }
              }}
              className={`w-full text-left px-1 ${compact ? "py-0.5 text-xs" : "py-1 text-sm"} font-medium ${
                !value || String(value).trim() === ""
                  ? subText
                  : darkMode
                    ? "text-[#E0E0E0]"
                    : "text-gray-900"
              }`}
            >
              {!value || String(value).trim() === ""
                ? monthYearEmptyLabel || formatMonthLabel(viewMonth, viewYear)
                : formatMonthLabel(viewMonth, viewYear)}
            </div>
          ) : (
          /*
            When monthYearOnly is true we hide the DD input entirely so
            the trigger shows "MM - YYYY". When false we show DD MMM YYYY.
          */
         <>
          {!monthYearOnly && (
            <>
          {/* Day input */}
          <input
            ref={dayRef}
            type="text"
            inputMode="numeric"
            placeholder=" "
            maxLength={2}
            onChange={(e) => handlePartChange('day', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, monthRef, null)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            className={`w-7 text-center bg-transparent outline-none font-medium placeholder:text-transparent ${
              darkMode ? "placeholder-gray-400" : "placeholder-gray-500"
            }`}
          />
          <span className={`mx-0.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}> </span>
            </>
          )}
          {/* Month input */}
          <input
            ref={monthRef}
            type="text"
            inputMode={monthYearOnly ? "numeric" : "text"}
            placeholder=" "
            readOnly={monthYearOnly}
            maxLength={monthYearOnly ? 2 : 3}
            onChange={(e) => { if(!monthYearOnly) handlePartChange('month', e.target.value)}}
            onKeyDown={(e) => handleKeyDown(e, yearRef, dayRef)}
            onPaste={handlePaste}
            onFocus={(e) => { { if(!monthYearOnly)
              e.target.select();}
            }}
            disabled={disabled}
            className={`${monthYearOnly ? "w-7" : "w-10"} text-center bg-transparent outline-none font-medium placeholder:text-transparent ${
              darkMode ? "placeholder-gray-400" : "placeholder-gray-500"
            }`}
          />
          <span className={`mx-0.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{monthYearOnly ? "-" : " "}</span>
          
          {/* Year input */}
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            placeholder=" "
            maxLength={4}
            readOnly={monthYearOnly}
            onChange={(e) =>{ if(!monthYearOnly) handlePartChange('year', e.target.value)}}
            onKeyDown={(e) => handleKeyDown(e, null, monthRef)}
            onPaste={handlePaste}
            onFocus={(e) => {
              e.target.select();
            }}
            onBlur={() => { if(!monthYearOnly){
              // Validate year when user leaves the field
              const currentYear = yearRef.current?.value || "";
              const currentDay = dayRef.current?.value || "";
              const currentMonth = monthRef.current?.value || "";
              
              if (currentYear && currentYear.length > 0 && currentYear.length !== 4) {
                setTypingError("Year must be 4 digits");
              } else {
                validateDatePart(currentDay, currentMonth, currentYear);
              }
            }
            }}
            disabled={disabled}
            className={`w-11 text-center bg-transparent outline-none font-medium placeholder:text-transparent ${
              darkMode ? "placeholder-gray-400" : "placeholder-gray-500"
            }`}
          />
          </>
          )}
        </div>
      </div>

      {/* Calendar UI — portaled in compact/toolbar mode so it is not clipped */}
      {open &&
        !disabled &&
        (() => {
          const anchoredClass =
            dropdownAlign === "auto"
              ? dropdownPosition === "right"
                ? "right-0"
                : "left-0"
              : dropdownAlign === "right"
                ? "right-0"
                : "left-0";
          const panel = (
        <div
          ref={panelRef}
          className={`${portal ? "fixed" : "absolute"} z-[10350] ${portal ? "" : "mt-2"} w-80 max-w-[min(20rem,calc(100vw-1.5rem))] rounded shadow-xl border ${headerBorder} ${headerBg} ${
            portal ? "" : anchoredClass
          } ${dropdownClassName || ""}`}
          style={
            portal
              ? { top: panelCoords.top, left: panelCoords.left }
              : undefined
          }
          role="dialog"
          aria-label={`${label || "date"} calendar`}
        >
          {/* Header with consistent button styles */}
          <div className={`flex items-center justify-between p-3 border-b ${headerBorder}`}>
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              {(() => {
                // compute if prev is allowed
                let prevDisabled = false;
                if (view === "year") {
                  const prevStart = yearStart - 12;
                  const prevEnd = yearStart - 1;
                  const minYearAllowed = allowedMin.getFullYear();
                  prevDisabled = prevStart < minYearAllowed && prevEnd < minYearAllowed;
                } else {
                  const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
                  const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
                  const prevEnd = new Date(prevYear, prevMonth + 1, 0);
                  prevDisabled = prevEnd < allowedMin;
                }
                return (
                  <button
                    className={`px-2 py-1 rounded ${prevDisabled ? "opacity-40 cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                    onClick={() => {
                      if (prevDisabled) return;
                      if (view === "year") {
                        setViewYear(yearStart - 12);
                      } else if (view === "month") {
                        const newMonth = viewMonth - 1;
                        if (newMonth < 0) {
                          setViewYear((y) => y - 1);
                          setViewMonth(11);
                        } else setViewMonth(newMonth);
                      } else {
                        const m = viewMonth - 1;
                        if (m < 0) {
                          setViewMonth(11);
                          setViewYear((y) => y - 1);
                        } else setViewMonth(m);
                      }
                    }}
                    aria-label="Previous"
                  >
                    ‹
                  </button>
                );
              })()}

              {/* Month button toggles month grid; keep same style */}
              <button
                className={`px-3 py-1 rounded font-medium ${textCol}`}
                onClick={() => setView((v) => (monthYearOnly ? "month" : v === "month" ? "day" : "month"))}
              >
                {months[viewMonth]}
              </button>

              {/* Year button always visible on top; clicking goes to year selection */}
              <button
                className={`px-3 py-1 rounded ${subText}`}
                onClick={() => setView("year")}
              >
                {viewYear}
              </button>

              {/* Next button */}
              {(() => {
                // compute if next is allowed
                let nextDisabled = false;
                if (view === "year") {
                  const nextStart = yearStart + 12;
                  const nextEnd = yearStart + 23;
                  const maxYearAllowed = allowedMax.getFullYear();
                  nextDisabled = nextStart > maxYearAllowed && nextEnd > maxYearAllowed;
                } else {
                  const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
                  const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
                  const nextStart = new Date(nextYear, nextMonth, 1);
                  nextDisabled = nextStart > allowedMax;
                }
                return (
                  <button
                    className={`px-2 py-1 rounded ${nextDisabled ? "opacity-40 cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                    onClick={() => {
                      if (nextDisabled) return;
                      if (view === "year") {
                        setViewYear(yearStart + 12);
                      } else if (view === "month") {
                        const newMonth = viewMonth + 1;
                        if (newMonth > 11) {
                          setViewYear((y) => y + 1);
                          setViewMonth(0);
                        } else setViewMonth(newMonth);
                      } else {
                        const m = viewMonth + 1;
                        if (m > 11) {
                          setViewMonth(0);
                          setViewYear((y) => y + 1);
                        } else setViewMonth(m);
                      }
                    }}
                    aria-label="Next"
                  >
                    ›
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Calendar views remain exactly the same */}
          {view === "year" ? (
            <div className="p-3">
              <div className="grid grid-cols-3 gap-2">
                {years.map((y) => {
                  const minYearAllowed = allowedMin.getFullYear();
                  const maxYearAllowed = allowedMax.getFullYear();
                  const outOfRange = y < minYearAllowed || y > maxYearAllowed;
                  return (
                    <button
                      key={y}
                      disabled={outOfRange}
                      className={`py-2 rounded text-sm w-full ${
                        outOfRange
                          ? darkMode
                            ? "bg-[#2C2C2C] text-gray-600 cursor-not-allowed"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : y === viewYear
                          ? darkMode
                            ? "bg-[#BB86FC] text-black"
                            : "rh-btn-primary"
                          : darkMode
                          ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                          : "bg-white text-gray-900 border border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        if (outOfRange) return;
                        let mm = viewMonth;
                        const first = new Date(y, mm, 1);
                        const last = new Date(y, mm + 1, 0);
                        if (last < allowedMin) {
                          mm = allowedMin.getMonth();
                        } else if (first > allowedMax) {
                          mm = allowedMax.getMonth();
                        }
                        setViewYear(y);
                        setViewMonth(mm);
                        setView("month");
                      }}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : view === "month" ? (
            <div className="p-3">
              <div className="grid grid-cols-3 gap-2">
                {months.map((m, idx) => {
                  const monthStart = new Date(viewYear, idx, 1);
                  const monthEnd = new Date(viewYear, idx + 1, 0);
                  const outOfRange = monthEnd < allowedMin || monthStart > allowedMax;
                  return (
                    <button
                      key={m}
                      disabled={outOfRange}
                      className={`py-2 rounded text-sm w-full ${
                        outOfRange
                          ? darkMode
                            ? "bg-[#2C2C2C] text-gray-600 cursor-not-allowed"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : idx === viewMonth
                          ? darkMode
                            ? "bg-[#BB86FC] text-black"
                            : "rh-btn-primary"
                          : darkMode
                          ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                          : "bg-white text-gray-900 border border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        if (outOfRange) return;

                        if (monthYearOnly) {
                          const yearNum = viewYear;
                          const monthIndex = idx;
                          const first = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-01`;
                          const lastDay = daysInMonth(yearNum, monthIndex);
                          const last = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

                          // visual updates
                          if (monthRef.current) monthRef.current.value = String(monthIndex + 1).padStart(2, "0");
                          if (yearRef.current) yearRef.current.value = String(yearNum);
                          if (dayRef.current) dayRef.current.value = "";

                          setInputText(formatMonthLabel(monthIndex, yearNum));
                          onChange?.({ target: { value: `${first}/${last}` } });
                          setViewYear(yearNum);
                          setViewMonth(monthIndex);
                          setOpen(false);
                          setTouched(true);
                          if (required) setTypingError("");
                          return;
                        }

                        // normal behavior (day selection view)
                        setViewMonth(idx);
                        setView("day");
                      }}

                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-3">
              <div className={`grid grid-cols-7 text-center text-xs ${subText} mb-2`}>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const lead = firstDayIndex(viewYear, viewMonth);
                  const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
                  const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
                  const prevDays = daysInMonth(prevYear, prevMonth);
                  const cells = [];
                  for (let i = lead - 1; i >= 0; i--) {
                    const day = prevDays - i;
                    const date = new Date(prevYear, prevMonth, day);
                    const s = stripTime(date);
                    const outOfRange = isBeforeMin(s) || isAfterMax(s);
                    cells.push(
                      <button
                        key={`prev-${day}`}
                        disabled={outOfRange}
                        className={`h-8 rounded text-sm flex items-center justify-center ${darkMode ? "text-gray-500" : "text-gray-400"} ${outOfRange ? "cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                        onClick={() => !outOfRange && selectDate(date)}
                      >
                        {String(day).padStart(2, "0")}
                      </button>
                    );
                  }
                  const thisDays = daysInMonth(viewYear, viewMonth);
                  for (let d = 1; d <= thisDays; d++) {
                    const date = new Date(viewYear, viewMonth, d);
                    const s = stripTime(date);
                    const disabledDay = isBeforeMin(s) || isAfterMax(s);
                    const isSelected = value && new Date(value).toDateString() === date.toDateString();
                    cells.push(
                      <button
                        key={`cur-${d}`}
                        disabled={disabledDay}
                        className={`h-8 rounded text-sm flex items-center justify-center ${
                          disabledDay
                            ? darkMode
                              ? "text-gray-500 cursor-not-allowed"
                              : "text-gray-400 cursor-not-allowed"
                            : isSelected
                              ? darkMode
                                ? "bg-[#BB86FC] text-black"
                                : "rh-btn-primary"
                              : darkMode
                                ? "hover:bg-[#3C3C3C] text-[#E0E0E0]"
                                : "hover:bg-gray-100 text-gray-900"
                          }`}
                        onClick={() => selectDay(d)}
                      >
                        {String(d).padStart(2, "0")}
                      </button>
                    );
                  }
                  const rem = cells.length % 7;
                  const tailCount = rem === 0 ? 0 : 7 - rem;
                  const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
                  const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
                  for (let d = 1; d <= tailCount; d++) {
                    const date = new Date(nextYear, nextMonth, d);
                    const s = stripTime(date);
                    const outOfRange = isBeforeMin(s) || isAfterMax(s);
                    cells.push(
                      <button
                        key={`next-${d}`}
                        disabled={outOfRange}
                        className={`h-7 rounded text-xs flex items-center justify-center ${darkMode ? "text-gray-500" : "text-gray-400"} ${outOfRange ? "cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                        onClick={() => !outOfRange && selectDate(date)}
                      >
                        {String(d).padStart(2, "0")}
                      </button>
                    );
                  }
                  return cells;
                })()}
              </div>
              <div className={`mt-3 flex items-center justify-between border-t ${headerBorder} pt-2`}>
                {(() => {
                  const now = new Date();
                  const s = stripTime(now);
                  const todayDisabled = isBeforeMin(s) || isAfterMax(s);
                  return (
                    <button
                      disabled={todayDisabled}
                      className={`px-2 py-1 rounded text-sm ${
                        todayDisabled
                          ? 'opacity-40 cursor-not-allowed'
                          : darkMode
                          ? 'hover:bg-[#3C3C3C]'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        if (todayDisabled) return;
                        if (monthYearOnly) {
                          const yearNum = today.getFullYear();
                          const monthIndex = today.getMonth();
                          const first = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-01`;
                          const lastDay = daysInMonth(yearNum, monthIndex);
                          const last = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

                          if (monthRef.current) monthRef.current.value = String(monthIndex + 1).padStart(2, "0");
                          if (yearRef.current) yearRef.current.value = String(yearNum);
                          if (dayRef.current) dayRef.current.value = "";

                          setInputText(formatMonthLabel(monthIndex, yearNum));
                          onChange?.({ target: { value: `${first}/${last}` } });
                          setViewYear(yearNum);
                          setViewMonth(monthIndex);
                          setOpen(false);
                          return;
                        }

                        // FIXED: Update input fields when clicking Today
                        const today = new Date();
                        if (dayRef.current) dayRef.current.value = String(today.getDate()).padStart(2, "0");
                        if (monthRef.current) monthRef.current.value = MONTH_ABBREV[today.getMonth()];
                        if (yearRef.current) yearRef.current.value = String(today.getFullYear());
                        
                        setInputText(toDisplay(today));
                        onChange?.({ target: { value: toISO(today) } });
                        setViewYear(today.getFullYear());
                        setViewMonth(today.getMonth());
                        setOpen(false);
                        setTouched(true);
                        if (required) setTypingError("");
                      }}
                    >
                      Today
                    </button>
                  );
                })()}
                <button
                  className={`px-2 py-1 rounded text-sm ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                  onClick={() => {
                    // If required, prevent clearing and show error
                    if (required) {
                      setTouched(true);
                      setTypingError("This field is required");
                      return;
                    }
                    onChange?.({ target: { value: "" } });
                    setInputText("");
                    setOpen(false);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
          );
          return portal && typeof document !== "undefined"
            ? createPortal(panel, document.body)
            : panel;
        })()}
      {(error || typingError) ? (
        <div className="mt-0.5 text-xs text-red-600" aria-live="polite">
          {error || typingError}
        </div>
      ) : null}
    </div>
  );
} 